import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post } from '@/lib/api/client';
import type {
  PromoCodeInput,
  PromoCodeSearchResult,
  Redemption,
} from './types';

/**
 * Promo codes.
 *
 * `discountcode` and `discountcode/store` are two routes on one action with one
 * binding model, so there is no separate "store code" endpoint — a code is a
 * store code when it carries no `userId`. Only the create route is a real
 * write: `discountcode/edit` takes `{ id, expiresAt }` and nothing else, so an
 * existing code's value, limits and scope are fixed once saved.
 */

const KEY = ['promo-codes'] as const;

export const PAGE_SIZE = 20;

export type CodeKind = 'store' | 'customer';

export function usePromoCodes(params: {
  kind: CodeKind;
  query: string;
  businessId: string | null;
  page: number;
}) {
  const trimmed = params.query.trim();

  return useQuery({
    queryKey: [...KEY, params.kind, trimmed, params.businessId, params.page],
    queryFn: () =>
      post<PromoCodeSearchResult>(
        params.kind === 'store'
          ? 'businesses/search/discountcode/stores'
          : 'businesses/search/discountcode/users',
        {
          page: params.page,
          size: PAGE_SIZE,
          query: trimmed,
          // Only the store search takes it; the user search ignores the field.
          ...(params.kind === 'store'
            ? { businessId: params.businessId ?? '' }
            : {}),
        }
      ),
    staleTime: 30_000,
  });
}

/** Who has used a code, and on which order. */
export function useRedemptions(codeId: string | null) {
  return useQuery({
    queryKey: [...KEY, 'redemptions', codeId],
    queryFn: () =>
      get<Redemption[]>(`businesses/discountcode/redemptions/${codeId}`),
    enabled: codeId !== null,
  });
}

function useInvalidate() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: KEY });
}

export function useCreatePromoCode() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (values: PromoCodeInput) =>
      post<unknown>('businesses/discountcode', values),
    onSuccess: invalidate,
  });
}

/**
 * The only edit there is.
 *
 * `EditCouponExpirationDateModel` carries an id and a date, so nothing else
 * about a saved code can be changed — which is why the editor shows the rest
 * as read-only rather than as fields that silently discard what you type.
 */
export function useUpdateExpiry() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: ({ id, expiresAt }: { id: string; expiresAt: string | null }) =>
      post<unknown>('businesses/discountcode/edit', { id, expiresAt }),
    onSuccess: invalidate,
  });
}

export function useArchivePromoCode() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (id: string) =>
      post<unknown>(`businesses/discountcode/archive/${id}`, {}),
    onSuccess: invalidate,
  });
}
